/**
 * La liste des enfants, chargée une fois pour toutes les coquilles d'adulte.
 *
 * **Dans `ui/` et non dans `parent/`** depuis la V2 (24 septembre 2026) :
 * `src/v2/` n'a pas le droit d'importer `parent/`, et sa première page est un
 * choix d'enfant. Déplacée plutôt que recopiée — deux caches de la même liste
 * finiraient par se rafraîchir à des moments différents, et les deux coquilles
 * afficheraient deux barres de prénoms qui ne disent pas la même chose.
 *
 * Elle est devenue une donnée de **navigation** le 6 septembre 2026 : les
 * onglets du haut portent les prénoms (« Tableau de bord · Baya · Mehdi · À
 * valider · Réglages »), donc la barre est rendue sur chaque écran parent. Sans
 * cache, changer d'onglet redemanderait la liste au serveur à chaque clic, et
 * la barre clignoterait entre deux écrans qui affichent la même chose.
 *
 * Un cache de module et non un contexte React : la liste ne change pas pendant
 * une session — un enfant n'est ni ajouté ni retiré depuis la PWA — et un
 * contexte imposerait un fournisseur à chaque test de rendu.
 *
 * `listChildren` n'est pas journalisée côté serveur, à dessein : *« savoir que
 * ses enfants existent n'est pas consulter leurs données »* (`core-api` §5.2).
 * La mettre dans la barre de navigation ne change donc rien au journal d'audit.
 */

import { useEffect, useState } from "react";

import { call } from "@/api/client";
import type { ChildSummary } from "@/api/schema";

let enCours: Promise<ChildSummary[]> | null = null;

export function chargerLesEnfants(): Promise<ChildSummary[]> {
  // Un échec ne se garde pas : une coupure réseau au premier rendu condamnerait
  // la barre de navigation pour toute la session.
  enCours ??= call("listChildren").catch((echec) => {
    enCours = null;
    throw echec;
  });
  return enCours;
}

/** Vider le cache — pour les tests, et à la déconnexion. */
export function oublierLesEnfants(): void {
  enCours = null;
}

/**
 * Les enfants, ou une liste vide.
 *
 * **Aucune erreur remontée** : c'est le contrat de ce hook, et il est délibéré.
 * L'appelant est une barre de navigation ; si la liste ne charge pas, il faut
 * qu'il reste « Tableau de bord », « À valider » et « Réglages » plutôt qu'un
 * message d'erreur en travers de l'écran qu'on était en train de lire.
 */
export function useEnfants(): ChildSummary[] {
  const [enfants, setEnfants] = useState<ChildSummary[]>([]);

  useEffect(() => {
    let annule = false;
    void chargerLesEnfants()
      .then((liste) => {
        if (!annule) setEnfants(liste);
      })
      .catch(() => undefined);
    return () => {
      annule = true;
    };
  }, []);

  return enfants;
}
